/**
 * Analytics dashboard registry + guest-token mint.
 *
 * Thin client for the embedded-Superset endpoints (api-service-go
 * superset_handlers.go). Dashboard visibility is decided entirely server-side
 * per caller (OpenFGA `feature:analytics.<slug>` Check, or-gated by the
 * view-tier `can_view_admin` relation for dashboards that opt in) — the
 * frontend renders whatever the registry returns and asks for nothing else.
 */

import { api } from '../client';
import type { Page } from '../types';

export interface AnalyticsDashboard {
  slug: string;
  /** i18n key for the tab label, e.g. "admin.analytics.dash.overview". */
  title_key: string;
  /** Live Superset EmbeddedDashboard uuid the embedded-sdk mounts. */
  embedded_uuid: string;
  /** Browser-facing Superset origin (e.g. http://localhost:8097). */
  embed_host: string;
}

/**
 * GET /analytics/dashboards — the one list envelope. The registry is read whole
 * from config, so the response carries no `Link: rel="next"`.
 */
export type AnalyticsDashboardsResponse = Page<AnalyticsDashboard>;

export interface GuestTokenResponse {
  token: string;
}

export const analyticsApi = {
  getDashboards: () => api.getPage<AnalyticsDashboardsResponse>('/analytics/dashboards'),

  // Minting a guest token issues an ephemeral credential and mutates nothing
  // server-side, so — unlike the admin grant/revoke POSTs — it carries no
  // Idempotency-Key. The embedded-sdk re-invokes this before every ~5-minute
  // token expiry; each call re-runs the FGA Check live (never cached).
  getGuestToken: (slug: string) =>
    api.post<GuestTokenResponse>(`/analytics/dashboards/${slug}/guest-token`),
};
